import { BACKEND_KEYS, MODULE_BACKEND_ROUTING } from './backendRoutingTable.js';
import {assertC6Endpoint} from './c6Isolation.js';

const DEFAULT_PHP_API_BASE_URL = '/api';
const DEFAULT_NODE_API_BASE_URL = '/node';

const metaEnv = (typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env : {};

const API_BASES = {
    [BACKEND_KEYS.PHP]: metaEnv.VITE_PHP_API_BASE_URL || DEFAULT_PHP_API_BASE_URL,
    [BACKEND_KEYS.NODE]: metaEnv.VITE_NODE_API_BASE_URL || DEFAULT_NODE_API_BASE_URL,
};

const MODULE_TARGETS = {
    auth: metaEnv.VITE_AUTH_API_TARGET,
    push: metaEnv.VITE_PUSH_API_TARGET,
    dashboard: metaEnv.VITE_DASHBOARD_API_TARGET,
    perfil: metaEnv.VITE_PERFIL_API_TARGET,
    credenciales: metaEnv.VITE_CREDENCIALES_API_TARGET,
    notificaciones: metaEnv.VITE_NOTIFICACIONES_API_TARGET,
    visitas: metaEnv.VITE_VISITAS_API_TARGET,
    informes: metaEnv.VITE_INFORMES_API_TARGET,
    observaciones: metaEnv.VITE_OBSERVACIONES_API_TARGET,
    solicitudes: metaEnv.VITE_SOLICITUDES_API_TARGET,
    activosTecnicos: metaEnv.VITE_ACTIVOS_API_TARGET,
    tracking: metaEnv.VITE_TRACKING_API_TARGET,
};

function normalizePath(pathValue) {
    if (!pathValue) {
        return '';
    }

    return pathValue.startsWith('/') ? pathValue : `/${pathValue}`;
}

function resolveModuleConfig(moduleName) {
    return MODULE_BACKEND_ROUTING[moduleName] || {
        defaultBackend: BACKEND_KEYS.PHP,
        fallbackOrder: [BACKEND_KEYS.PHP, BACKEND_KEYS.NODE],
        endpoints: {},
    };
}

function getSupportedBackends(moduleConfig) {
    return new Set([
        moduleConfig.defaultBackend || BACKEND_KEYS.PHP,
        ...(moduleConfig.fallbackOrder || []),
    ]);
}

function resolvePrimaryBackend(moduleName) {
    const moduleConfig = resolveModuleConfig(moduleName);
    const target = MODULE_TARGETS[moduleName] || moduleConfig.defaultBackend || BACKEND_KEYS.PHP;
    const supportedBackends = getSupportedBackends(moduleConfig);

    if (!supportedBackends.has(target)) {
        return moduleConfig.defaultBackend || BACKEND_KEYS.PHP;
    }

    if (API_BASES[target]) {
        return target;
    }

    return BACKEND_KEYS.PHP;
}

function uniqueOrderedBackends(primaryBackend, fallbackOrder) {
    const unique = new Set([primaryBackend, ...(fallbackOrder || [])]);
    return Array.from(unique).filter((backend) => Boolean(API_BASES[backend]));
}

function resolveEndpointBackends(moduleConfig, endpointPathOrKey, options = {}) {
    if (!options.isEndpointKey) {
        return null;
    }

    const endpointConfig = moduleConfig.endpoints?.[endpointPathOrKey];
    const endpointBackends = endpointConfig?.backends;

    if (!Array.isArray(endpointBackends) || endpointBackends.length === 0) {
        return null;
    }

    return endpointBackends.filter((backend) => Boolean(API_BASES[backend]));
}

export function getApiTargetForModule(moduleName) {
    return resolvePrimaryBackend(moduleName);
}

export function getApiBaseForModule(moduleName) {
    const target = resolvePrimaryBackend(moduleName);
    return API_BASES[target] || API_BASES[BACKEND_KEYS.PHP];
}

export function buildEndpointPath(moduleName, endpointKey, params = {}) {
    const moduleConfig = resolveModuleConfig(moduleName);
    const endpointConfig = moduleConfig.endpoints?.[endpointKey];
    const rawPath = endpointConfig?.path || endpointKey || '';

    return Object.entries(params).reduce((acc, [paramKey, value]) => {
        return acc.replace(`{${paramKey}}`, encodeURIComponent(String(value)));
    }, rawPath);
}

export function getEndpointCandidates(moduleName, endpointPathOrKey, options = {}) {
    const primary = resolvePrimaryBackend(moduleName);
    const moduleConfig = resolveModuleConfig(moduleName);
    const path = options.isEndpointKey
        ? buildEndpointPath(moduleName, endpointPathOrKey, options.pathParams || {})
        : endpointPathOrKey;

    const normalizedPath = normalizePath(path);
    const endpointBackends = resolveEndpointBackends(moduleConfig, endpointPathOrKey, options);
    const fallbacks = endpointBackends && endpointBackends.length > 0
        ? endpointBackends
        : uniqueOrderedBackends(primary, moduleConfig.fallbackOrder);

    return fallbacks.map((backend) => {
        const url=`${API_BASES[backend]}${normalizedPath}`;
        assertC6Endpoint(url,metaEnv.VITE_C6_ISOLATED_ORIGIN);
        return {backend,url};
    });
}
